// Lógica pura do dashboard Afastamento e inatividade (S-2230).
// buildRequestBody: estado da tela -> corpo camelCase dos endpoints do BFF.
// buildChartVM: DTO de dashboard do BFF -> view-model dos gráficos da página.

export interface AfastamentoFiltros {
  cnpjList: string[];
  startDate?: string;
  endDate?: string;
  regimeList: string[];
  categoriaList: number[];
  motivoList: number[];
  cargoList: string[];
  cboList: string[];
}

export interface AfastamentoState {
  afastados: boolean;
  inativos: boolean;
  descanso: boolean;
  semMotivo: boolean;
  busca: string;
  filtros: AfastamentoFiltros;
}

export interface RequestBody {
  retiredCollaboratorsToggle: boolean;
  activeCollaboratorsToggle: boolean;
  vacationCollaboratorsToggle: boolean;
  reasonNotInformedToggle: boolean;
  cnpjList: string[];
  categoryCodeList: number[];
  codMotAfastList: number[];
  cboCodeList: string[];
  nmCargoList: string[];
  tpRegPrevList: string[];
  startDate?: string;
  endDate?: string;
  filterTerm?: string;
}

export function buildRequestBody(s: AfastamentoState): RequestBody {
  const term = s.busca.trim();
  return {
    retiredCollaboratorsToggle: s.afastados,
    activeCollaboratorsToggle: s.inativos, // wire: inativos vai como activeCollaboratorsToggle
    vacationCollaboratorsToggle: s.descanso,
    reasonNotInformedToggle: s.semMotivo,
    cnpjList: s.filtros.cnpjList,
    categoryCodeList: s.filtros.categoriaList,
    codMotAfastList: s.filtros.motivoList,
    cboCodeList: s.filtros.cboList,
    nmCargoList: s.filtros.cargoList,
    tpRegPrevList: s.filtros.regimeList,
    startDate: s.filtros.startDate,
    endDate: s.filtros.endDate,
    filterTerm: term || undefined,
  };
}

// ---- Dashboard DTO (espelha o response do BFF) ----

export interface DashboardDTO {
  activeRetiredProportion?: { retiredCount: number; inactiveCount: number };
  reasonGroupProportion?: {
    paidLeaveCount: number;
    retirementCount: number;
    inactivityCount: number;
  };
  categoryProportion?: {
    codeCategory: number;
    category: string | null;
    categoryCount: number;
    categoryPercentage?: string;
  }[];
  codMotAfastProportion?: {
    codMotAfast: string;
    motivoAfastamento: string | null;
    codMotAfastCount: number;
    codMotAfastPercentage?: string;
  }[];
}

export const COR = {
  Afastamento: '#E4C23F',
  'Descanso remunerado': '#3B5BDB',
  Inatividade: '#E03131',
  'Motivo não informado': '#868E96',
  // Categoria usa roxo p/ não confundir com o amarelo do tipo de afastamento (CON-5289).
  Categoria: '#7048E8',
} as const;

// CON-5290: cor da barra por código do motivo de afastamento — azul p/ férias/licença remunerada
// (15, 16), vermelho p/ inatividade (06, 34), amarelo p/ os demais. `codMotAfast` é string c/ zero à esquerda.
const MOTIVO_AZUL = ['15', '16'];
const MOTIVO_VERMELHO = ['06', '34'];
function corMotivo(cod?: string): string {
  if (cod && MOTIVO_AZUL.includes(cod)) return COR['Descanso remunerado'];
  if (cod && MOTIVO_VERMELHO.includes(cod)) return COR.Inatividade;
  return COR.Afastamento;
}

export interface Barra {
  label: string;
  count: number;
  valor: number;
  cor?: string;
}

export interface ChartVM {
  total: number;
  visaoGeral: Barra[];
  situacao: Barra[];
  informado: { count: number; valor: number };
  naoInformado: { count: number; valor: number };
  categoria: Barra[];
  motivos: Barra[];
}

const pct = (n: number, total: number) => (total ? (n / total) * 100 : 0);

export function buildChartVM(d?: DashboardDTO): ChartVM {
  const rg = d?.reasonGroupProportion ?? {
    paidLeaveCount: 0,
    retirementCount: 0,
    inactivityCount: 0,
  };
  const codmot = d?.codMotAfastProportion ?? [];

  const naoInformadoCount = codmot
    .filter((c) => c.codMotAfast === '00')
    .reduce((acc, c) => acc + (c.codMotAfastCount ?? 0), 0);
  const informadoCount = codmot
    .filter((c) => c.codMotAfast !== '00')
    .reduce((acc, c) => acc + (c.codMotAfastCount ?? 0), 0);
  const total = informadoCount + naoInformadoCount;

  const total3 = rg.retirementCount + rg.paidLeaveCount + rg.inactivityCount;
  const situacao: Barra[] = [
    {
      label: 'Afastamento',
      count: rg.retirementCount,
      valor: pct(rg.retirementCount, total3),
      cor: COR.Afastamento,
    },
    {
      label: 'Descanso remunerado',
      count: rg.paidLeaveCount,
      valor: pct(rg.paidLeaveCount, total3),
      cor: COR['Descanso remunerado'],
    },
    {
      label: 'Inatividade',
      count: rg.inactivityCount,
      valor: pct(rg.inactivityCount, total3),
      cor: COR.Inatividade,
    },
  ];

  const total4 = total3 + naoInformadoCount;
  const visaoGeral: Barra[] = [
    {
      label: 'Afastamento',
      count: rg.retirementCount,
      valor: pct(rg.retirementCount, total4),
      cor: COR.Afastamento,
    },
    {
      label: 'Descanso remunerado',
      count: rg.paidLeaveCount,
      valor: pct(rg.paidLeaveCount, total4),
      cor: COR['Descanso remunerado'],
    },
    {
      label: 'Inatividade',
      count: rg.inactivityCount,
      valor: pct(rg.inactivityCount, total4),
      cor: COR.Inatividade,
    },
    {
      label: 'Motivo não informado',
      count: naoInformadoCount,
      valor: pct(naoInformadoCount, total4),
      cor: COR['Motivo não informado'],
    },
  ];

  const cat = d?.categoryProportion ?? [];
  const catTotal = cat.reduce((acc, c) => acc + (c.categoryCount ?? 0), 0);
  const categoria: Barra[] = cat.map((c) => ({
    label: `${c.codeCategory} - ${c.category ?? ''}`.trim(),
    count: c.categoryCount,
    valor: pct(c.categoryCount, catTotal),
    cor: COR.Categoria,
  }));

  const motTotal = codmot.reduce((acc, c) => acc + (c.codMotAfastCount ?? 0), 0);
  const motivos: Barra[] = codmot.map((c) => ({
    label: `${c.codMotAfast} - ${c.motivoAfastamento ?? ''}`.trim(),
    count: c.codMotAfastCount,
    valor: pct(c.codMotAfastCount, motTotal),
    cor: corMotivo(c.codMotAfast),
  }));

  return {
    total,
    visaoGeral,
    situacao,
    informado: { count: informadoCount, valor: pct(informadoCount, total) },
    naoInformado: { count: naoInformadoCount, valor: pct(naoInformadoCount, total) },
    categoria,
    motivos,
  };
}
